import dynamic from "@redux-dynostore/react-redux";
import subspaced from "@redux-dynostore/react-redux-subspace";
import { useTranslation } from "react-i18next";
import styled, { withTheme } from "styled-components";
import { attachReducer } from "@redux-dynostore/redux-subspace";
import React, { useEffect, useState, useCallback } from "react";
import { connect } from "react-redux";
import CustomModal from "../../ui-library/CustomModal/CustomModal";
import Targets from "../../library/Goals/Targets";
import SetDailyGoals from "../../library/SetDailyGoals/SetDailyGoals";
import {
  targetsReducer,
  mapDispatchToProps,
  mapStateToProps,
} from "./Targets.store";
import PrimaryButton from "../../ui-library/Buttons/PrimaryButton/PrimaryButton";
import { validDateString } from "../../../modules/utils";
import { sendNotification } from "../../ToastManager/ToastManager";
import { ToastType } from "../../ToastManager/constants";
import SmartDailyTargetsHistory from "../DailyTargetsHistory/DailyTargetsHistory";
import { isRtl } from "../../../i18n/helpers";
import { useMemo } from "react";

const StyledFooter = styled.div`
  display: flex;
  width: 100%;
  margin-top: 40px;
  justify-content: flex-end;

  @media (max-width: 576px) {
    padding-top: 2rem;
    margin-top: auto;
    .btn {
      width: 100%;
    }
  }
`;

export const SmartTargets = (props) => {
  const {
    goalsList,
    canViewTargetsHistory,
    canCreateNewTarget,
    fetchGoals,
    userData,
    dateRange: { startDate = null, endDate = null } = {},
    resetState,
    participantId,
    updateDailyTargets,
    currentDailyTargets,
    update,
  } = props;

  const { t } = useTranslation();

  const [showUpdateDailyTargets, setShowUpdateDailyTargets] = useState(false);
  const [dailyTargets, setDailyTargets] = useState(null);
  const [showDailyTargetHistory, setShowDailyTargetHistory] = useState(false);

  const submitForm = useCallback(() => {
    updateDailyTargets(
      participantId,
      dailyTargets,
      dailyTargets.reasonForChange
    )
      .then((message) => {
        sendNotification(message, ToastType.SUCCESS);
        closeTargetsModal();
        setDailyTargets(null);
        fetchGoals(startDate, endDate, participantId, t).catch((error) => {
          sendNotification(error, ToastType.ERROR);
        });
      })
      .catch((error) => {
        sendNotification(error, ToastType.ERROR);
      });
  }, [
    dailyTargets,
    endDate,
    fetchGoals,
    participantId,
    startDate,
    updateDailyTargets,
    t,
  ]);

  const onChangeDailyTargets = useCallback((valid, goals) => {
    setDailyTargets(valid ? goals : null);
  }, []);

  useEffect(() => {
    if (validDateString(startDate) && validDateString(endDate)) {
      fetchGoals(startDate, endDate, participantId, t).catch((error) => {
        sendNotification(error, ToastType.ERROR);
      });
    }
  }, [endDate, fetchGoals, startDate, participantId, t]);

  useEffect(() => {
    if (
      update &&
      validDateString(startDate) &&
      validDateString(endDate) &&
      userData?.studyStatus
    ) {
      fetchGoals(startDate, endDate, participantId, t).catch((error) => {
        sendNotification(error, ToastType.ERROR);
      });
    }
  }, [endDate, fetchGoals, participantId, startDate, update, t]);

  useEffect(() => {
    return () => resetState();
  }, [resetState]);

  const closeTargetsModal = () => {
    setShowUpdateDailyTargets(false);
  };

  const closeHistoryModal = () => {
    setShowDailyTargetHistory(false);
  };

  const targetHistoryTitle = useMemo(() => {
    let ugnxId = userData?.participantUgnxId ?? "";

    if (isRtl()) {
      const ugnxIdSplitted = ugnxId.split("-");
      if (ugnxIdSplitted.length === 2) {
        ugnxId = `${ugnxIdSplitted[1]}-${ugnxIdSplitted[0]}`;
      }
    }

    return `${t("appText.dietDiary.targets.history.title", {
      ugnxId: ugnxId,
    })}`;
  }, [userData]);

  return (
    <>
      <Targets
        goalsList={goalsList ? Object.values(goalsList) : null}
        {...{ canCreateNewTarget }}
        {...{ canViewTargetsHistory }}
        onEdit={() => setShowUpdateDailyTargets(true)}
        onViewHistory={() => setShowDailyTargetHistory(true)}
        isRtl={isRtl()}
      />

      <CustomModal
        show={showUpdateDailyTargets}
        title={`Daily target for ${userData?.participantUgnxId ?? ""}`}
        onClose={closeTargetsModal}
      >
        <SetDailyGoals
          onChange={onChangeDailyTargets}
          editing={true}
          defaultTargets={currentDailyTargets}
        />
        <StyledFooter>
          <PrimaryButton
            data-testid="save-targets-button"
            onClick={() => submitForm()}
            disabled={!dailyTargets}
          >
            Create A New Target
          </PrimaryButton>
        </StyledFooter>
      </CustomModal>

      <CustomModal
        show={showDailyTargetHistory}
        title={targetHistoryTitle}
        onClose={closeHistoryModal}
      >
        <SmartDailyTargetsHistory
          dailyTargetId={currentDailyTargets?.key}
          onClose={closeHistoryModal}
        />
      </CustomModal>
    </>
  );
};

export default withTheme(
  dynamic(
    "targetsReducer",
    subspaced({
      // We map extra state because we might need to acess other stores for data, subspace is self contained.
      mapExtraState: (state, rootState) => ({
        loginReducer: state.loginReducer,
      }),
    }),
    attachReducer(targetsReducer)
  )(connect(mapStateToProps, mapDispatchToProps)(SmartTargets))
);
